import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import AuthContext from "./AuthContext";
import { getAuthToken, clearAuthToken, storeAuthToken } from "../utils/storage";
import * as authService from "../api/auth";

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const decodeToken = (token) => {
    try {
      const decoded = jwtDecode(token);
      return {
        user_id: decoded.user_id,
        tenant_id: decoded.tenant_id,
        roles: decoded.roles || [],
        permissions: decoded.permissions || [],
        exp: decoded.exp,
      };
    } catch (err) {
      console.error("Failed to decode token:", err);
      return null;
    }
  };

  const mergeWithProfile = async (base) => {
    try {
      const profile = await authService.getMe();
      return {
        ...base,
        email_verified_at: profile?.email_verified_at ?? null,
        firstname: profile?.firstname,
        lastname: profile?.lastname,
        profile,
      };
    } catch {
      return base;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const token = getAuthToken();
      if (token) {
        const decoded = decodeToken(token);
        if (decoded && Date.now() / 1000 < decoded.exp) {
          const merged = await mergeWithProfile(decoded);
          setUser(merged);
          setIsAuthenticated(true);
        } else {
          clearAuthToken();
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials) => {
    try {
      const { token } = await authService.login(credentials);
      storeAuthToken(token);
      const decoded = decodeToken(token);
      const merged = await mergeWithProfile(decoded);
      setUser(merged);
      setIsAuthenticated(true);
      return { success: true, user: merged };
    } catch (error) {
      return { success: false, error };
    }
  };

  const signup = async (userData) => {
    try {
      const response = await authService.signup(userData);
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error };
    }
  };

  const logout = () => {
    clearAuthToken();
    setIsAuthenticated(false);
    setUser(null);
  };

  const refreshProfile = async () => {
    const token = getAuthToken();
    if (!token) return;
    const decoded = decodeToken(token);
    if (!decoded) return;
    const merged = await mergeWithProfile(decoded);
    setUser(merged);
  };

  const canManageTenantUsers = (tenantId) => {
    if (user?.roles?.includes("super_admin")) {
      return true;
    }
    if (user?.tenant_id === tenantId) {
      return true;
    }
    return false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        canManageTenantUsers,
        isLoading,
        login,
        signup,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
